import type { CategoriaDeMeta, ConsumoDeWhatsApp, CosteDeWhatsApp, CostePorMensaje, DetalleDeExtracto, DiferenciaDeCoste, ImportacionDeExtracto, LineaDeExtracto, RecargaDelMonedero, ResolucionDeDiferencia } from '../../api/ops/backoffice';
import type { NombreIcono } from '../../componentes/iconos';
import { dinero, simboloMoneda } from '../../logica/dinero';
import { claveMes, fechaLarga, nombreMes, type Mes } from '../../partners/logica';

/**
 * Lógica de **Conciliación YCloud** (1.22, `1.20-1.22-backoffice.html?seccion=ycloud`), sin nada visual: textos del
 * prototipo (`yRows`, `RES`, `RATE_CATS`, `vy`, `fr`) y cómo se cruzan el extracto, sus diferencias y lo que hemos contado.
 * El panel no suma ni calcula dinero (G121, G290): cada importe es uno que da la API.
 */

export const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'] as const;

/** Hoy en Madrid: año, mes (0-11) y día. */
export function hoyEnMadrid(ahora: Date): Mes & { d: number } {
    const partes = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(ahora);
    const v = (tipo: string) => Number(partes.find((p) => p.type === tipo)?.value ?? '0');
    return { y: v('year'), m: v('month') - 1, d: v('day') };
}

/** El mes que se ve al entrar: el anterior al de hoy (el último con extracto completo), como el prototipo. */
export function mesInicial(ahora: Date): Mes {
    const h = hoyEnMadrid(ahora);
    return h.m === 0 ? { y: h.y - 1, m: 11 } : { y: h.y, m: h.m - 1 };
}

/** Un mes posterior al de hoy no se puede elegir. */
export function mesFuturo(mes: Mes, ahora: Date): boolean {
    const h = hoyEnMadrid(ahora);
    return mes.y > h.y || (mes.y === h.y && mes.m > h.m);
}

/** `period` del extracto y de `getWhatsAppUsageSummary` (`AAAA-MM`). */
export const periodoDe = claveMes;

/** «septiembre de 2026». */
export const textoDelMes = (mes: Mes): string => `${nombreMes(mes)} de ${String(mes.y)}`;

/** Categoría de Meta en las filas («Servicio»). */
export const CATEGORIAS: Readonly<Record<CategoriaDeMeta, string>> = { marketing: 'Marketing', utility: 'Utilidad', authentication: 'Autenticación', service: 'Servicio' };

/** Categorías del coste por mensaje, en el orden del prototipo (`RATE_CATS`). */
export const CATEGORIAS_COSTE: readonly { valor: CategoriaDeMeta; texto: string }[] = [
    { valor: 'marketing', texto: 'Marketing' },
    { valor: 'utility', texto: 'Utilidad' },
    { valor: 'authentication', texto: 'Autenticación' },
    { valor: 'service', texto: 'Servicio (respuesta en 24 h)' },
];

export const PAISES: readonly { valor: string; texto: string }[] = [
    { valor: 'ES', texto: 'España' },
    { valor: 'PT', texto: 'Portugal' },
    { valor: 'FR', texto: 'Francia' },
];

/** Las cuatro formas de resolver una diferencia (prototipo `RES`): título, botón, icono y lo que queda en la fila. */
export const RESOLUCIONES: Readonly<Record<ResolucionDeDiferencia, { titulo: string; boton: string; icono: NombreIcono; hecha: string; texto: string }>> = {
    record_corrected: { titulo: 'Corregir el registro', boton: 'Corregir', icono: 'pencil', hecha: 'Corregido', texto: 'Nuestro registro estaba mal y se corrige.' },
    balance_adjusted: { titulo: 'Ajustar el paquete del negocio', boton: 'Ajustar', icono: 'sliders-horizontal', hecha: 'Paquete ajustado', texto: 'Se crea un movimiento de ajuste en el paquete del negocio.' },
    claimed: { titulo: 'Reclamar a YCloud', boton: 'Reclamar', icono: 'send', hecha: 'Reclamado a YCloud', texto: 'Se reclama a YCloud. Queda abierta hasta que respondan.' },
    absorbed: { titulo: 'Asumir el coste', boton: 'Asumir', icono: 'wallet', hecha: 'Asumido por Reservaria', texto: 'Reservaria asume el coste. El negocio no ve ningún cambio.' },
};
export const ORDEN_RESOLUCIONES: readonly ResolucionDeDiferencia[] = ['record_corrected', 'balance_adjusted', 'claimed', 'absorbed'];

/** «+2,50 €» (con signo más si YCloud cobra de más), «−1,00 €» o «0,00 €». */
export function textoDiferencia(d: DiferenciaDeCoste['difference']): string {
    return d.amount > 0 ? `+${dinero(d)}` : dinero(d);
}

/** Una fila de la tabla: la línea del extracto (o la diferencia sin línea) con lo que hemos contado y su diferencia. */
export interface FilaConciliacion {
    clave: string;
    negocio: string;
    categoria: string;
    ycloud: string;
    nosotros: string;
    diferencia: string;
    /** Diferencia por resolver (también la reclamada a YCloud sin respuesta, G1153): en rojo y con sus acciones. */
    abierta: boolean;
    /** Reclamada a YCloud y sin respuesta (`claimed`, G1153): sigue abierta, sin volver a «Reclamar». */
    reclamada: boolean;
    /** Lo que se hizo con ella («Corregido»…), o null. */
    hecha: string | null;
    /** Con la marca de que cuadra o ya está resuelta. */
    bien: boolean;
    discrepancia: DiferenciaDeCoste | null;
}

/** Las formas de resolver que quedan: una reclamada ya no se vuelve a reclamar (G1153). */
export function resolucionesDe(fila: Pick<FilaConciliacion, 'reclamada'>): ResolucionDeDiferencia[] {
    return ORDEN_RESOLUCIONES.filter((r) => !(fila.reclamada && r === 'claimed'));
}

/**
 * Las filas del mes: una por línea del extracto y, aparte, las diferencias sin línea (lo que contamos y YCloud no cobra).
 * «Nosotros» es el coste de `getWhatsAppUsageSummary` del mismo canal (o negocio), categoría y país: cada categoría va a
 * un solo paquete (G47), así que hay como mucho un total; si no lo hay, «—». La diferencia, la de la API; sin diferencia y
 * con el extracto ya conciliado, «0,00 €». El nombre del negocio llega en la línea y en la diferencia (`business_name`,
 * G1140); sin él, la cuenta de WhatsApp.
 */
export function filasConciliacion(extracto: DetalleDeExtracto, diferencias: readonly DiferenciaDeCoste[], consumo: readonly ConsumoDeWhatsApp[]): FilaConciliacion[] {
    const conciliado = extracto.status !== 'imported';
    const cero = dinero({ amount: 0, currency: extracto.total_excl_tax.currency });
    const nombre = (n: string | null | undefined, respaldo: string): string => (n == null || n === '' ? respaldo : n);
    const nuestro = (l: LineaDeExtracto): string => {
        const iguales = consumo.filter((c) => (l.channel_ref != null ? c.channel_ref === l.channel_ref : l.business_id != null && c.business_id === l.business_id) && c.pricing_category === l.pricing_category && c.pricing_country === l.country);
        const uno = iguales.length === 1 ? iguales[0] : undefined;
        return uno?.cost == null ? '—' : dinero(uno.cost);
    };
    const fila = (clave: string, negocio: string, categoria: CategoriaDeMeta, ycloud: string, nosotros: string, d: DiferenciaDeCoste | undefined): FilaConciliacion => {
        const reclamada = d?.status === 'claimed';
        const abierta = d?.status === 'open' || reclamada;
        const hecha = reclamada ? 'Reclamado a YCloud · sin respuesta' : d !== undefined && d.status === 'resolved' && d.resolution != null ? RESOLUCIONES[d.resolution].hecha : null;
        return {
            clave,
            negocio,
            categoria: CATEGORIAS[categoria],
            ycloud,
            nosotros,
            diferencia: d !== undefined ? textoDiferencia(d.difference) : conciliado ? cero : '—',
            abierta,
            reclamada,
            hecha,
            bien: !abierta && (d !== undefined || conciliado),
            discrepancia: d ?? null,
        };
    };
    const lineas = new Set(extracto.lines.map((l) => l.id));
    return [
        ...extracto.lines.map((l) => fila(l.id, nombre(l.business_name, l.waba_ref), l.pricing_category, dinero(l.amount), nuestro(l), diferencias.find((d) => d.line_id === l.id))),
        ...diferencias.filter((d) => d.line_id == null || !lineas.has(d.line_id)).map((d) => fila(d.id, nombre(d.business_name, '—'), d.pricing_category, '—', '—', d)),
    ];
}

/** Texto de la ventana de resolver (prototipo `vy`). */
export const textoResolver = (fila: Pick<FilaConciliacion, 'negocio' | 'categoria' | 'diferencia'>, r: ResolucionDeDiferencia): string => `${fila.negocio} · ${fila.categoria} · diferencia de ${fila.diferencia}. ${RESOLUCIONES[r].texto}`;

/** Mensajes del ajuste del paquete: un entero distinto de 0, con signo (negativo descuenta). */
export function mensajesDeAjuste(texto: string): number | null {
    const t = texto.trim().replace(/^\+/, '').replace('−', '-');
    if (!/^-?\d{1,9}$/.test(t)) {
        return null;
    }
    const n = Number(t);
    return n === 0 ? null : n;
}

/** «0,0585 €»: el coste por mensaje con 4 decimales (`UnitCost`, en diezmilésimas, G1149). */
export function costePorMensaje(u: CostePorMensaje): string {
    return `${(u.amount / 10_000).toLocaleString('es-ES', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ${simboloMoneda(u.currency)}`;
}

/** «0,0612» → 612 diezmilésimas (G1149); null si no es un coste mayor que 0 con hasta 4 decimales. */
export function aDiezmilesimas(texto: string): number | null {
    const t = texto.replace(/\s|€/g, '').replace(',', '.');
    if (!/^\d+(\.\d{1,4})?$/.test(t)) {
        return null;
    }
    const [enteros = '0', decimales = ''] = t.split('.');
    const n = Number(enteros) * 10_000 + Number(decimales.padEnd(4, '0'));
    return n > 0 ? n : null;
}

/** Una fila de «Coste por mensaje de Meta» (prototipo `rates`). */
export interface FilaCoste {
    categoria: string;
    coste: string;
    desde: string;
    programado: boolean;
}

/**
 * Por categoría: el coste vigente o, si hay uno que empieza más adelante, ese con «Programado» y el anterior. Los costes
 * no se editan: uno nuevo cierra el anterior (`valid_to`).
 */
export function filasCoste(costes: readonly CosteDeWhatsApp[], ahora: Date): FilaCoste[] {
    const t = ahora.getTime();
    return CATEGORIAS_COSTE.map(({ valor, texto }) => {
        const deEsta = costes.filter((c) => c.pricing_category === valor);
        const vigente = deEsta.filter((c) => Date.parse(c.valid_from) <= t && (c.valid_to == null || Date.parse(c.valid_to) > t)).sort((a, b) => Date.parse(b.valid_from) - Date.parse(a.valid_from))[0];
        const futuro = deEsta.filter((c) => Date.parse(c.valid_from) > t).sort((a, b) => Date.parse(b.valid_from) - Date.parse(a.valid_from))[0];
        if (futuro !== undefined) {
            return { categoria: texto, coste: costePorMensaje(futuro.unit_cost), desde: vigente === undefined ? `Desde el ${fechaLarga(futuro.valid_from)}` : `Desde el ${fechaLarga(futuro.valid_from)} · el anterior, ${costePorMensaje(vigente.unit_cost)}`, programado: true };
        }
        return { categoria: texto, coste: vigente === undefined ? '—' : costePorMensaje(vigente.unit_cost), desde: vigente === undefined ? '' : `Desde el ${fechaLarga(vigente.valid_from)}`, programado: false };
    });
}

/** Desfase de Madrid en una fecha («+01:00»). */
function desfaseMadrid(utc: number): string {
    const nombre = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Madrid', timeZoneName: 'longOffset' }).formatToParts(new Date(utc)).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+01:00';
    const m = /([+-]\d{2}:\d{2})/.exec(nombre);
    return m?.[1] ?? '+00:00';
}

/**
 * «Vale desde» (prototipo `fromOpts`): el primer día de los dos próximos trimestres, a las 00:00 de Madrid, que es cuando
 * Meta cambia las tarifas.
 */
export function iniciosDeTrimestre(ahora: Date): { valor: string; texto: string }[] {
    const h = hoyEnMadrid(ahora);
    const primero = Math.floor(h.m / 3) * 3 + 3;
    return [primero, primero + 3].map((mm) => {
        const y = h.y + Math.floor(mm / 12);
        const m = mm % 12;
        const fecha = `${String(y)}-${String(m + 1).padStart(2, '0')}-01`;
        return { valor: `${fecha}T00:00:00${desfaseMadrid(Date.UTC(y, m, 1, 12))}`, texto: `1 de ${nombreMes({ y, m })} de ${String(y)}` };
    });
}

export const ERROR_COSTE = 'Escribe el coste, por ejemplo 0,0612.';
export const ERROR_MENSAJES = 'Escribe cuántos mensajes.';
export const NOTA_COSTE = 'No se edita: el coste nuevo cierra el anterior de la misma categoría y país. No cambia lo que descuenta cada mensaje de su paquete.';

// ── Cerrar el mes (G1152, G1153)

/** Lo que dice el extracto sobre el cierre del mes: cerrado (con la fecha), se puede cerrar o por qué no. */
// Provisional hasta C.67 (textos).
export function estadoCierre(e: Pick<DetalleDeExtracto, 'status' | 'closed_at' | 'open_discrepancies'>): { cerrado: boolean; puede: boolean; texto: string } {
    if (e.status === 'closed') {
        return { cerrado: true, puede: false, texto: e.closed_at == null ? 'Mes cerrado.' : `Mes cerrado el ${fechaLarga(e.closed_at)}.` };
    }
    if (e.status === 'imported') {
        return { cerrado: false, puede: false, texto: 'No se puede cerrar el mes: el extracto aún no está conciliado.' };
    }
    if (e.open_discrepancies > 0) {
        const n = e.open_discrepancies;
        return {
            cerrado: false,
            puede: false,
            texto: `No se puede cerrar el mes: ${n === 1 ? 'queda 1 diferencia abierta' : `quedan ${String(n)} diferencias abiertas`}. Las reclamadas a YCloud siguen abiertas hasta que respondan.`,
        };
    }
    return { cerrado: false, puede: true, texto: 'No quedan diferencias abiertas: ya se puede cerrar el mes.' };
}

// ── Importar el extracto de YCloud (G1156): el CSV que se descarga de YCloud, convertido en líneas.

export type LineaImportada = ImportacionDeExtracto['lines'][number];

export interface CsvLeido {
    lineas: LineaImportada[];
    /** «Fila 3: falta el país.» */
    errores: string[];
}

const CATEGORIA_CSV: readonly [RegExp, CategoriaDeMeta][] = [
    [/marketing/i, 'marketing'],
    [/utilit|utilidad/i, 'utility'],
    [/auth|autentic/i, 'authentication'],
    [/servic/i, 'service'],
];

/**
 * Columnas que se reconocen en la cabecera del CSV, sin mayúsculas ni espacios. **Provisional hasta C.68**: el diseño no
 * dice cómo es el CSV de YCloud; se aceptan los nombres más probables en inglés y en español.
 */
// Provisional hasta C.68.
const COLUMNAS: Readonly<Record<'waba' | 'categoria' | 'pais' | 'mensajes' | 'importe', readonly string[]>> = {
    waba: ['waba_id', 'wabaid', 'waba', 'whatsapp_business_account_id', 'cuenta', 'account'],
    categoria: ['pricing_category', 'category', 'conversation_category', 'categoria', 'categoría'],
    pais: ['country', 'country_code', 'pricing_country', 'pais', 'país'],
    mensajes: ['billable_messages', 'billable', 'messages', 'quantity', 'count', 'mensajes'],
    importe: ['amount', 'cost', 'total', 'charge', 'importe', 'coste'],
};

/** Parte una línea de CSV respetando las comillas. */
function celdas(linea: string, separador: string): string[] {
    const fuera: string[] = [];
    let actual = '';
    let comillas = false;
    for (let i = 0; i < linea.length; i++) {
        const c = linea[i];
        if (c === '"') {
            if (comillas && linea[i + 1] === '"') {
                actual += '"';
                i++;
            } else {
                comillas = !comillas;
            }
        } else if (c === separador && !comillas) {
            fuera.push(actual.trim());
            actual = '';
        } else {
            actual += c ?? '';
        }
    }
    fuera.push(actual.trim());
    return fuera;
}

/** «12.34», «12,34», «1.234,56» o «1,234.56» → céntimos; null si no es un importe. */
function centimosCsv(texto: string): number | null {
    let t = texto.replace(/[\s€$]|USD|EUR/gi, '');
    const coma = t.lastIndexOf(',');
    const punto = t.lastIndexOf('.');
    if (coma > punto) {
        t = t.replace(/\./g, '').replace(',', '.');
    } else {
        t = t.replace(/,/g, '');
    }
    if (!/^\d+(\.\d+)?$/.test(t)) {
        return null;
    }
    return Math.round(Number.parseFloat(t) * 100);
}

/**
 * Lee el CSV de YCloud (separado por comas o por punto y coma, con cabecera) y lo convierte en las líneas de
 * `importWhatsAppCostStatement`: cuenta de WhatsApp, categoría de Meta, país, mensajes facturables e importe. Las filas
 * que no se entienden salen como errores con su número (la cabecera es la fila 1).
 */
export function leerCsvYCloud(texto: string, moneda = 'EUR'): CsvLeido {
    const filas = texto.replace(/^\uFEFF/, '').split(/\r?\n/).filter((l) => l.trim() !== '');
    const cabecera = filas[0];
    if (cabecera === undefined) {
        return { lineas: [], errores: ['El archivo está vacío.'] };
    }
    const separador = (cabecera.match(/;/g) ?? []).length > (cabecera.match(/,/g) ?? []).length ? ';' : ',';
    const nombres = celdas(cabecera, separador).map((c) => c.toLowerCase().replace(/\s+/g, '_'));
    const indice = Object.fromEntries(Object.entries(COLUMNAS).map(([clave, alias]) => [clave, nombres.findIndex((n) => alias.includes(n))])) as Record<keyof typeof COLUMNAS, number>;
    const faltan = Object.entries(indice).filter(([, i]) => i < 0).map(([clave]) => ({ waba: 'la cuenta de WhatsApp (waba_id)', categoria: 'la categoría', pais: 'el país', mensajes: 'los mensajes facturables', importe: 'el importe' })[clave as keyof typeof COLUMNAS]);
    if (faltan.length > 0) {
        return { lineas: [], errores: [`No encontramos en la cabecera ${faltan.join(', ')}. ¿Es el CSV de facturación de YCloud?`] };
    }
    const lineas: LineaImportada[] = [];
    const errores: string[] = [];
    filas.slice(1).forEach((fila, i) => {
        const n = i + 2;
        const c = celdas(fila, separador);
        const waba = c[indice.waba] ?? '';
        const categoria = CATEGORIA_CSV.find(([re]) => re.test(c[indice.categoria] ?? ''))?.[1];
        const pais = (c[indice.pais] ?? '').toUpperCase();
        const mensajes = Number((c[indice.mensajes] ?? '').replace(/[.\s]/g, ''));
        const importe = centimosCsv(c[indice.importe] ?? '');
        const fallo =
            waba === ''
                ? 'falta la cuenta de WhatsApp'
                : categoria === undefined
                  ? `no conocemos la categoría «${c[indice.categoria] ?? ''}»`
                  : !/^[A-Z]{2}$/.test(pais)
                    ? 'el país no es un código de dos letras'
                    : !Number.isInteger(mensajes) || mensajes < 0
                      ? 'los mensajes no son un número'
                      : importe === null
                        ? 'el importe no es un número'
                        : null;
        if (fallo !== null || categoria === undefined || importe === null) {
            errores.push(`Fila ${String(n)}: ${fallo ?? 'no se entiende'}.`);
            return;
        }
        lineas.push({ waba_ref: waba, pricing_category: categoria, country: pais, billable_messages: mensajes, amount: { amount: importe, currency: moneda } });
    });
    if (lineas.length === 0 && errores.length === 0) {
        errores.push('El archivo no tiene líneas.');
    }
    return { lineas, errores };
}

// ── Recargas del monedero (G1157, G1154): en dólares.

export const MONEDA_MONEDERO = 'USD';

/** «3 de octubre de 2026 · 500,00 US$ · TRF-2026-10-03». */
export function filaRecarga(r: RecargaDelMonedero): { fecha: string; importe: string; referencia: string } {
    return { fecha: fechaLarga(r.topped_up_at), importe: dinero(r.amount), referencia: r.reference };
}

export const ERROR_REFERENCIA_RECARGA = 'Escribe la referencia del pago.';
